import { Injectable } from '@angular/core';
import {
  DashboardData,
  DashboardPeriodOption,
  EngagementDonutMetric,
  ProgressMetric,
  RankingItem,
} from '../../models/interfaces/dashboard/dashboard.interface';
import { dashboardDataMock } from '../../mocks/interfaces/dashboard.mock';

@Injectable({
  providedIn: 'root',
})
export class DashboardService {

  private readonly data: DashboardData = dashboardDataMock;

  public getDashboardData(): DashboardData {
    return this.data;
  }

  public getPeriodOptions(): DashboardPeriodOption[] {
    return this.data.periodOptions;
  }

  public getEngagementDonuts(period: string): EngagementDonutMetric[] {
    return this.applyPeriodVariation(this.data.engagement.donuts, period);
  }

  public getEngagementProgress(period: string): ProgressMetric[] {
    return this.applyPeriodVariation(this.data.engagement.progress, period);
  }

  public getMostWatchedLessons(period: string): RankingItem[] {
    return this.applyRankingVariation(this.data.rankings.mostWatchedLessons, period);
  }

  public getMostAccessedCourses(period: string): RankingItem[] {
    return this.applyRankingVariation(this.data.rankings.mostAccessedCourses, period);
  }

  public getHighestCompletionCourses(period: string): RankingItem[] {
    return this.applyRankingVariation(this.data.rankings.highestCompletionCourses, period);
  }

  private applyPeriodVariation<T extends { percentage: number; absoluteValue: number }>(
    items: T[],
    period: string,
  ): T[] {
    const factor = this.getPeriodFactor(period);

    return items.map((item) => ({
      ...item,
      percentage: Math.min(100, Math.round(item.percentage * factor)),
      absoluteValue: Math.round(item.absoluteValue * factor),
    }));
  }

  private applyRankingVariation(items: RankingItem[], period: string): RankingItem[] {
    const factor = this.getPeriodFactor(period);

    return items.map((item) => ({
      ...item,
      value: item.isPercentage
        ? Math.min(100, Math.round(item.value * factor))
        : Math.max(1, Math.round(item.value * factor)),
    }));
  }

  private getPeriodFactor(period: string): number {
    if (period.includes('year'))
      return 1.15;

    if (period.endsWith('-01'))
      return 0.92;

    if (period.endsWith('-02'))
      return 1;

    return 1.05;
  }

}
